import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../api";
import ProjectMemos, { formatMemoWhen } from "../ProjectMemos";
import DocumentTypeList from "./DocumentTypeList";
import {
  STATUS_LABEL, STATUS_CLASS, ROLES, ROLE_LABEL, COMPANY_TYPE_LABEL,
  TASK_STATUS_LABEL, PRIORITIES, PRIORITY_LABEL, PRIORITY_CLASS,
} from "../projectMeta";

// 이 프로젝트에 연결된 서류를 종류별 탭으로 보여준다
const DOC_TABS = [
  ["QUOTE", "견적서"],
  ["PO", "발주서"],
  ["INVOICE", "인보이스"],
  ["CONTRACT", "계약서"],
];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState("");
  // ?tab=memo — 홈의 '메모' 버튼에서 프로젝트를 골라 들어오면 메모 탭부터 연다
  const [tab, setTab] = useState(() => (searchParams.get("tab") === "memo" ? "memo" : "info"));
  // 기본정보 요약용
  const [mainCompany, setMainCompany] = useState(null);
  const [mainContact, setMainContact] = useState(null);
  const [costTotal, setCostTotal] = useState(null);
  const [recentMemos, setRecentMemos] = useState(null);
  const [openMemoId, setOpenMemoId] = useState(null); // 기본정보에서 누른 메모장

  async function load() {
    setError("");
    try {
      setProject(await api.get(`/projects/${id}`));
    } catch (err) {
      setError(err.message);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 기본정보 요약: 주업체+담당자, 사용 비용 합계
  useEffect(() => {
    if (!project) return;
    const links = project.company_links || [];
    const primary = links.find((l) => l.is_primary) || links[0] || null;
    (async () => {
      if (primary && primary.company) {
        try {
          const c = await api.get(`/companies/${primary.company.id}`);
          setMainCompany(c);
          const cs = c.contacts || [];
          setMainContact(cs.find((x) => x.is_primary) || cs[0] || null);
        } catch { setMainCompany(null); setMainContact(null); }
      } else {
        setMainCompany(null); setMainContact(null);
      }
      try {
        const costs = await api.get(`/projects/${project.id}/costs`);
        setCostTotal(costs.reduce((s, r) => s + (r.amount != null ? r.amount : (Number(r.unit_price) || 0) * (Number(r.qty) || 0)), 0));
      } catch { setCostTotal(null); }
      loadRecentMemos();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  async function loadRecentMemos() {
    try {
      setRecentMemos(await api.get(`/projects/${id}/memos`));
    } catch { setRecentMemos(null); }
  }

  function openMemoTab(memoId) {
    setOpenMemoId(memoId || null);
    setTab("memo");
  }

  async function removeProject() {
    if (!window.confirm("이 프로젝트와 업체 연결을 삭제합니다. (업체 자체는 남습니다) 계속할까요?")) return;
    try {
      await api.del(`/projects/${id}`);
      navigate("/sstoa/projects", { replace: true });
    } catch (err) {
      setError(err.message);
    }
  }

  if (error) return <div className="sstoa-error">{error}</div>;
  if (!project) return <div className="sstoa-empty">불러오는 중…</div>;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to="/sstoa/projects" className="sstoa-back">← 프로젝트 목록</Link>
      </div>

      <div className="sstoa-topbar">
        <div>
          <h1 className="sstoa-title">
            {project.name}{" "}
            <span className={`sstoa-tag ${STATUS_CLASS[project.status] || ""}`} style={{ verticalAlign: "middle" }}>
              {STATUS_LABEL[project.status] || project.status}
            </span>
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Link to={`/sstoa/projects/${id}/edit`} className="sstoa-btn">수정</Link>
          <button className="sstoa-btn sstoa-btn--danger" onClick={removeProject}>삭제</button>
        </div>
      </div>

      <div className="sstoa-tabs" style={{ marginTop: 8 }}>
        <button type="button" className={`sstoa-tab ${tab === "info" ? "is-active" : ""}`} onClick={() => setTab("info")}>기본정보</button>
        <button type="button" className={`sstoa-tab ${tab === "companies" ? "is-active" : ""}`} onClick={() => setTab("companies")}>
          참여 업체<span className="sstoa-tab__cnt">{project.company_links.length}</span>
        </button>
        <button type="button" className={`sstoa-tab ${tab === "tasks" ? "is-active" : ""}`} onClick={() => setTab("tasks")}>일정</button>
        <button type="button" className={`sstoa-tab ${tab === "costs" ? "is-active" : ""}`} onClick={() => setTab("costs")}>비용</button>
        {DOC_TABS.map(([dt, label]) => (
          <button key={dt} type="button" className={`sstoa-tab ${tab === dt ? "is-active" : ""}`} onClick={() => setTab(dt)}>{label}</button>
        ))}
        <button type="button" className={`sstoa-tab ${tab === "memo" ? "is-active" : ""}`} onClick={() => openMemoTab(null)}>메모</button>
      </div>
      <div className="sstoa-tabpane">
        {tab === "info" && (
          <div className="sstoa-detail-cols" style={{ marginTop: 16 }}>
            <div className="sstoa-panel sstoa-panel--pad">
              <dl className="sstoa-meta">
                <dt>상태</dt><dd>{STATUS_LABEL[project.status] || project.status}</dd>
                <dt>시작일</dt><dd>{project.start_date || "—"}</dd>
                <dt>주업체</dt>
                <dd>
                  {mainCompany
                    ? <Link className="sstoa-link" to={`/sstoa/companies/${mainCompany.id}`}>{mainCompany.name}</Link>
                    : "—"}
                </dd>
                <dt>담당자</dt><dd>{mainContact ? mainContact.name : "—"}</dd>
                <dt>연락처</dt>
                <dd>{mainContact && mainContact.phone ? <a className="sstoa-link" href={`tel:${mainContact.phone.replace(/-/g, "")}`}>{mainContact.phone}</a> : "—"}</dd>
                <dt>이메일</dt>
                <dd>{mainContact && mainContact.email ? <Link className="sstoa-link" to={`/sstoa/email?to=${encodeURIComponent(mainContact.email)}`}>{mainContact.email}</Link> : "—"}</dd>
                <dt>사용 비용</dt>
                <dd style={{ fontWeight: 700 }}>{costTotal == null ? "…" : `${won(costTotal)} 원`}</dd>
              </dl>
            </div>
            <div className="sstoa-panel sstoa-panel--pad sstoa-memo">
              <div className="sstoa-memo__label">최근 메모장</div>
              {recentMemos && recentMemos.length > 0 ? (
                <ul className="sstoa-memo__list">
                  {recentMemos.slice(0, 5).map((m) => (
                    <li key={m.id}>
                      <button type="button" className="sstoa-link sstoa-memo__item" onClick={() => openMemoTab(m.id)}>{m.title}</button>
                      <span className="sstoa-memo__when">{formatMemoWhen(m.updated_at)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="sstoa-memo__body">{recentMemos ? "—" : "…"}</div>
              )}
            </div>
          </div>
        )}
        {tab === "companies" && <Companies projectId={id} links={project.company_links} onChange={load} />}
        {tab === "tasks" && <Tasks projectId={id} />}
        {tab === "costs" && <Costs projectId={id} />}
        {DOC_TABS.some(([dt]) => dt === tab) && <DocumentTypeList key={tab} docType={tab} projectId={id} />}
        {tab === "memo" && <ProjectMemos key={openMemoId || "list"} projectId={id} initialOpenId={openMemoId} onChange={loadRecentMemos} />}
      </div>
    </div>
  );
}

/* ---------------- 비용(ProjectCost) ---------------- */
const won = (n) => (Number(n) || 0).toLocaleString("ko-KR");
const comma = (v) => {
  const s = String(v ?? "").replace(/[^0-9]/g, "");
  return s === "" ? "" : Number(s).toLocaleString("ko-KR");
};
const EMPTY_COST = { item: "", detail: "", unit_price: "", qty: "", vendor: "", note: "" };

function Costs({ projectId }) {
  const [rows, setRows] = useState([{ ...EMPTY_COST }]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  // 마지막에 빈 행 1개를 항상 유지 (스프레드시트식 자동 추가)
  const withTrailingBlank = (list) => {
    const last = list[list.length - 1];
    if (!last || (last.item || "").trim() !== "") return [...list, { ...EMPTY_COST }];
    return list;
  };

  async function load() {
    setError("");
    try {
      const data = await api.get(`/projects/${projectId}/costs`);
      const mapped = data.map((r) => ({
        item: r.item || "", detail: r.detail || "",
        unit_price: r.unit_price ? String(r.unit_price) : "",
        qty: r.qty ? String(r.qty) : "",
        vendor: r.vendor || "", note: r.note || "",
      }));
      setRows(withTrailingBlank(mapped.length ? mapped : []));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const updText = (i, k) => (e) => {
    const v = e.target.value;
    setRows((list) => withTrailingBlank(list.map((r, idx) => (idx === i ? { ...r, [k]: v } : r))));
  };
  const updNum = (i, k, max) => (e) => {
    let v = e.target.value.replace(/[^0-9]/g, "");
    if (v !== "") v = String(Math.min(parseInt(v, 10), max));
    setRows((list) => list.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  };
  const removeRow = (i) => setRows((list) => {
    const next = list.filter((_, idx) => idx !== i);
    return withTrailingBlank(next.length ? next : []);
  });

  const total = rows.reduce((s, r) => s + (Number(r.unit_price) || 0) * (Number(r.qty) || 0), 0);

  async function save() {
    if (busy) return;
    setBusy(true); setError(""); setMsg("");
    try {
      const payload = rows
        .filter((r) => (r.item || "").trim())
        .map((r) => ({
          item: r.item.trim(),
          detail: r.detail || null,
          unit_price: Number(r.unit_price) || 0,
          qty: Number(r.qty) || 0,
          vendor: r.vendor || null,
          note: r.note || null,
        }));
      await api.post(`/projects/${projectId}/costs`, payload);
      await load();
      setMsg("저장되었습니다.");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sstoa-section">
      <div className="sstoa-section__head">
        <h2>비용</h2>
        <button type="button" className="sstoa-btn sstoa-btn--primary sstoa-btn--sm" onClick={save} disabled={busy || loading}>
          {busy ? "저장 중…" : "저장"}
        </button>
      </div>

      {error && <div className="sstoa-error" style={{ marginBottom: 10 }}>{error}</div>}
      {msg && <div className="sstoa-error" style={{ marginBottom: 10, background: "#e7f6ec", borderColor: "#b6e0c4", color: "#1f7a3d" }}>{msg}</div>}

      <div className="sstoa-panel" style={{ padding: 12 }}>
        {loading ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="sstoa-table sstoa-items">
              <colgroup>
                <col style={{ width: 40 }} />
                <col style={{ width: 160 }} />
                <col />
                <col style={{ width: 110 }} />
                <col style={{ width: 70 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 36 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="c">번호</th><th>항목</th><th>세부내역</th><th>단가</th><th>수량</th>
                  <th className="r">금액</th><th>구매처</th><th>비고</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const isBlankTrailing = i === rows.length - 1 && !(r.item || "").trim();
                  return (
                    <tr key={i} style={{ cursor: "default" }}>
                      <td className="c" style={{ color: "var(--s-fg-mute)" }}>{isBlankTrailing ? "" : i + 1}</td>
                      <td><input className="sstoa-input" value={r.item} onChange={updText(i, "item")} disabled={busy} /></td>
                      <td><input className="sstoa-input" value={r.detail} onChange={updText(i, "detail")} disabled={busy} /></td>
                      <td><input className="sstoa-input" inputMode="numeric" style={{ textAlign: "right" }} value={comma(r.unit_price)} onChange={updNum(i, "unit_price", 9999999999)} disabled={busy} /></td>
                      <td><input className="sstoa-input" inputMode="numeric" style={{ textAlign: "right" }} value={comma(r.qty)} onChange={updNum(i, "qty", 99999)} disabled={busy} /></td>
                      <td className="r" style={{ whiteSpace: "nowrap" }}>{won((Number(r.unit_price) || 0) * (Number(r.qty) || 0))}</td>
                      <td><input className="sstoa-input" value={r.vendor} onChange={updText(i, "vendor")} disabled={busy} /></td>
                      <td><input className="sstoa-input" value={r.note} onChange={updText(i, "note")} disabled={busy} /></td>
                      <td className="r">
                        {!isBlankTrailing && <button type="button" className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => removeRow(i)} disabled={busy}>×</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12, paddingRight: 4 }}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>총액 {won(total)} 원</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- 일정(Task) ---------------- */
const EMPTY_TASK = { title: "", status: "TODO", due_date: "", priority: "NORMAL", note: "" };

function Tasks({ projectId }) {
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_TASK);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function load() {
    setError("");
    try {
      setRows(await api.get(`/projects/${projectId}/tasks`));
    } catch (err) { setError(err.message); }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function add(e) {
    e.preventDefault();
    if (!form.title.trim()) { setError("제목은 필수입니다."); return; }
    setBusy(true); setError("");
    try {
      await api.post(`/projects/${projectId}/tasks`, { ...form, due_date: form.due_date || null });
      setForm(EMPTY_TASK); setAdding(false); load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function cycle(t) {
    const next = t.status === "TODO" ? "DOING" : t.status === "DOING" ? "DONE" : "TODO";
    try { await api.post(`/tasks/${t.id}`, { status: next }); load(); } catch (err) { setError(err.message); }
  }
  async function remove(tid) {
    if (!window.confirm("일정을 삭제할까요?")) return;
    try { await api.del(`/tasks/${tid}`); load(); } catch (err) { setError(err.message); }
  }

  return (
    <div className="sstoa-section">
      <div className="sstoa-section__head">
        <h2>일정</h2>
        {!adding && <button className="sstoa-btn sstoa-btn--sm" onClick={() => setAdding(true)}>+ 일정 추가</button>}
      </div>

      {error && <div className="sstoa-error" style={{ marginBottom: 10 }}>{error}</div>}

      {adding && (
        <form className="sstoa-panel sstoa-panel--pad" style={{ marginBottom: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }} onSubmit={add}>
          <div className="sstoa-field" style={{ flex: 2, minWidth: 180 }}>
            <label>제목</label>
            <input className="sstoa-input" value={form.title} onChange={upd("title")} disabled={busy} />
          </div>
          <div className="sstoa-field" style={{ minWidth: 130 }}>
            <label>마감일</label>
            <input className="sstoa-input" type="date" value={form.due_date} onChange={upd("due_date")} disabled={busy} />
          </div>
          <div className="sstoa-field" style={{ minWidth: 100 }}>
            <label>우선순위</label>
            <select className="sstoa-select" value={form.priority} onChange={upd("priority")} disabled={busy}>
              {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" className="sstoa-btn sstoa-btn--primary sstoa-btn--sm" disabled={busy}>추가</button>
            <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => { setAdding(false); setForm(EMPTY_TASK); }}>취소</button>
          </div>
        </form>
      )}

      <div className="sstoa-panel">
        {rows === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : rows.length === 0 ? (
          <div className="sstoa-empty">등록된 일정이 없습니다.</div>
        ) : (
          <table className="sstoa-table">
            <thead><tr><th>상태</th><th>제목</th><th>마감일</th><th>우선순위</th><th></th></tr></thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} style={{ cursor: "default" }}>
                  <td>
                    <button className={`sstoa-tag ${t.status === "DONE" ? "sstoa-tag--gray" : ""}`} style={{ border: 0, cursor: "pointer" }} onClick={() => cycle(t)} title="클릭하여 상태 변경">
                      {TASK_STATUS_LABEL[t.status] || t.status}
                    </button>
                  </td>
                  <td style={{ fontWeight: 600, textDecoration: t.status === "DONE" ? "line-through" : "none", color: t.status === "DONE" ? "var(--s-fg-mute)" : "inherit" }}>{t.title}</td>
                  <td>{t.due_date || "—"}</td>
                  <td><span className={`sstoa-tag ${PRIORITY_CLASS[t.priority] || ""}`}>{PRIORITY_LABEL[t.priority] || t.priority}</span></td>
                  <td style={{ textAlign: "right" }}>
                    <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => remove(t.id)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ---------------- 참여 업체 연결 ---------------- */
function Companies({ projectId, links, onChange }) {
  const [adding, setAdding] = useState(false);
  const [companies, setCompanies] = useState(null);
  const [companyId, setCompanyId] = useState("");
  const [role, setRole] = useState("CLIENT");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function openAdd() {
    setError("");
    setAdding(true);
    if (companies === null) {
      try {
        setCompanies(await api.get("/companies"));
      } catch (err) {
        setError(err.message);
      }
    }
  }

  async function add(e) {
    e.preventDefault();
    if (!companyId) { setError("업체를 선택하세요."); return; }
    setBusy(true); setError("");
    try {
      await api.post(`/projects/${projectId}/companies`, {
        company_id: Number(companyId),
        role,
        note: note || null,
      });
      setCompanyId(""); setRole("CLIENT"); setNote(""); setAdding(false);
      onChange();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  async function remove(linkId) {
    if (!window.confirm("업체 연결을 해제할까요? (업체 자체는 남습니다)")) return;
    try {
      await api.del(`/projects/${projectId}/companies/${linkId}`);
      onChange();
    } catch (err) { setError(err.message); }
  }

  return (
    <div className="sstoa-section">
      <div className="sstoa-section__head">
        <h2>참여 업체</h2>
        {!adding && <button className="sstoa-btn sstoa-btn--sm" onClick={openAdd}>+ 협력업체 추가</button>}
      </div>

      {error && <div className="sstoa-error" style={{ marginBottom: 10 }}>{error}</div>}

      {adding && (
        <form className="sstoa-panel sstoa-panel--pad" style={{ marginBottom: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }} onSubmit={add}>
          <div className="sstoa-field" style={{ flex: 2, minWidth: 180 }}>
            <label>업체</label>
            <select className="sstoa-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)} disabled={busy || companies === null}>
              <option value="">{companies === null ? "불러오는 중…" : "선택하세요"}</option>
              {(companies || []).map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({COMPANY_TYPE_LABEL[c.type] || c.type})</option>
              ))}
            </select>
          </div>
          <div className="sstoa-field" style={{ minWidth: 140 }}>
            <label>역할</label>
            <select className="sstoa-select" value={role} onChange={(e) => setRole(e.target.value)} disabled={busy}>
              {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="sstoa-field" style={{ flex: 2, minWidth: 140 }}>
            <label>비고 (선택)</label>
            <input className="sstoa-input" value={note} onChange={(e) => setNote(e.target.value)} disabled={busy} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" className="sstoa-btn sstoa-btn--primary" disabled={busy}>연결</button>
            <button type="button" className="sstoa-btn" onClick={() => setAdding(false)}>취소</button>
          </div>
        </form>
      )}

      <div className="sstoa-panel">
        {links.length === 0 ? (
          <div className="sstoa-empty">연결된 업체가 없습니다. 업체를 연결해 역할을 지정하세요.</div>
        ) : (
          <table className="sstoa-table">
            <thead><tr><th>업체</th><th>구분</th><th>역할</th><th>비고</th><th></th></tr></thead>
            <tbody>
              {[...links].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0)).map((lk) => (
                <tr key={lk.id} style={{ cursor: "default" }}>
                  <td style={{ fontWeight: 600 }}>
                    {lk.company ? (
                      <Link className="sstoa-link" to={`/sstoa/companies/${lk.company.id}`}>{lk.company.name}</Link>
                    ) : `#${lk.company_id}`}
                  </td>
                  <td>
                    {lk.is_primary
                      ? <span className="sstoa-tag">주업체</span>
                      : <span className="sstoa-tag sstoa-tag--gray">협력</span>}
                  </td>
                  <td><span className="sstoa-tag sstoa-tag--gray">{ROLE_LABEL[lk.role] || lk.role}</span></td>
                  <td>{lk.note || "—"}</td>
                  <td style={{ textAlign: "right" }}>
                    <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => remove(lk.id)}>해제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
